import { GameEngine, type ColorId, type Pos } from '@color-dots/game';
import type { Scene3DApp } from '../scene3d/Scene3DApp.js';

export type InputCallback = () => void;

type InputMode = 'idle' | 'draw' | 'rotate';

export class InputRouter {
  private activeColor: ColorId | null = null;
  private mode: InputMode = 'idle';
  private pointerId: number | null = null;
  private rotateBtnPointerId: number | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private scene3d: Scene3DApp,
    private engine: GameEngine,
    private onUpdate: InputCallback,
    private onWin: InputCallback,
    private rotateLeftBtn: HTMLElement,
    private rotateRightBtn: HTMLElement,
  ) {
    this.bindEvents();
  }

  getActiveColor(): ColorId | null {
    return this.activeColor;
  }

  isDrawing(): boolean {
    return this.mode === 'draw';
  }

  destroy(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('lostpointercapture', this.onPointerUp);
    this.rotateLeftBtn.removeEventListener('pointerdown', this.onRotateLeftDown);
    this.rotateLeftBtn.removeEventListener('pointerup', this.onRotateButtonUp);
    this.rotateLeftBtn.removeEventListener('pointercancel', this.onRotateButtonUp);
    this.rotateLeftBtn.removeEventListener('lostpointercapture', this.onRotateButtonUp);
    this.rotateRightBtn.removeEventListener('pointerdown', this.onRotateRightDown);
    this.rotateRightBtn.removeEventListener('pointerup', this.onRotateButtonUp);
    this.rotateRightBtn.removeEventListener('pointercancel', this.onRotateButtonUp);
    this.rotateRightBtn.removeEventListener('lostpointercapture', this.onRotateButtonUp);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  private bindEvents(): void {
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);
    this.canvas.addEventListener('lostpointercapture', this.onPointerUp);
    this.rotateLeftBtn.addEventListener('pointerdown', this.onRotateLeftDown);
    this.rotateLeftBtn.addEventListener('pointerup', this.onRotateButtonUp);
    this.rotateLeftBtn.addEventListener('pointercancel', this.onRotateButtonUp);
    this.rotateLeftBtn.addEventListener('lostpointercapture', this.onRotateButtonUp);
    this.rotateRightBtn.addEventListener('pointerdown', this.onRotateRightDown);
    this.rotateRightBtn.addEventListener('pointerup', this.onRotateButtonUp);
    this.rotateRightBtn.addEventListener('pointercancel', this.onRotateButtonUp);
    this.rotateRightBtn.addEventListener('lostpointercapture', this.onRotateButtonUp);
    window.addEventListener('keydown', this.onKeyDown);
  }

  private onRotateLeftDown = (event: PointerEvent): void => {
    this.startButtonRotate(event, -1);
  };

  private onRotateRightDown = (event: PointerEvent): void => {
    this.startButtonRotate(event, 1);
  };

  private onRotateButtonUp = (event: PointerEvent): void => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    if (this.rotateBtnPointerId !== event.pointerId) return;
    if (target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }
    this.rotateBtnPointerId = null;
    this.scene3d.rotation.stopButtonRotate();
  };

  private startButtonRotate(event: PointerEvent, direction: -1 | 1): void {
    if (this.mode === 'draw' || this.rotateBtnPointerId !== null) return;
    if (!(event.currentTarget instanceof HTMLElement)) return;
    event.preventDefault();
    this.rotateBtnPointerId = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);
    this.scene3d.rotation.startButtonRotate(direction);
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (this.mode === 'draw') return;
    if (event.key === 'ArrowLeft' || event.key === 'q' || event.key === 'Q') {
      this.scene3d.rotation.stepRotateByButton(-1);
    } else if (event.key === 'ArrowRight' || event.key === 'e' || event.key === 'E') {
      this.scene3d.rotation.stepRotateByButton(1);
    } else if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.engine.undo();
      this.onUpdate();
    }
  };

  private onPointerDown = (event: PointerEvent): void => {
    if (this.pointerId !== null || this.scene3d.rotation.isAnimating()) return;

    const pos = this.pickPos(event);
    const endpointColor = pos ? this.engine.getColorAtEndpoint(pos) : null;
    const cellColor = pos ? this.engine.getColorAtCell(pos) : null;

    if (endpointColor || cellColor) {
      this.startDraw(event, pos!, endpointColor ?? cellColor!);
      return;
    }

    this.startRotate(event);
  };

  private startDraw(event: PointerEvent, pos: Pos, color: ColorId): void {
    this.pointerId = event.pointerId;
    this.mode = 'draw';
    this.scene3d.rotation.setBlocked(true);
    this.canvas.setPointerCapture(event.pointerId);

    if (this.engine.getColorAtEndpoint(pos)) {
      this.activeColor = color;
      this.engine.startPath(color, pos);
      this.onUpdate();
    } else {
      this.activeColor = color;
    }

    this.scene3d.showTouchAt(event.clientX, event.clientY);
  }

  private startRotate(event: PointerEvent): void {
    this.pointerId = event.pointerId;
    this.mode = 'rotate';
    this.canvas.setPointerCapture(event.pointerId);
    this.scene3d.rotation.startDrag(event.clientX);
    this.scene3d.rotation.setDragStartY(event.clientY);
  }

  private onPointerMove = (event: PointerEvent): void => {
    if (this.pointerId !== event.pointerId) return;

    if (this.mode === 'draw' && this.activeColor) {
      const pos = this.pickPos(event);
      if (!pos) return;

      this.scene3d.showTouchAt(event.clientX, event.clientY);
      const result = this.engine.extendPath(this.activeColor, pos);
      if (result.ok) {
        this.onUpdate();
        if (result.won) {
          this.endPointer(event);
          this.onWin();
        }
      }
      return;
    }

    if (this.mode === 'rotate') {
      this.scene3d.rotation.updateDrag(event.clientX, event.clientY);
    }
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (this.pointerId !== event.pointerId) return;
    this.endPointer(event);
  };

  private endPointer(event: PointerEvent): void {
    if (this.canvas.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId);
    }

    if (this.mode === 'rotate') {
      this.scene3d.rotation.endDrag();
    }

    this.scene3d.hideTouch();
    this.scene3d.rotation.setBlocked(false);
    this.mode = 'idle';
    this.pointerId = null;
  }

  private pickPos(event: PointerEvent): Pos | null {
    return this.scene3d.pickCell(event.clientX, event.clientY);
  }
}
